/** Production hostnames for the marketing site and product app. */
export const PRODUCTION_MARKETING_HOST = 'unifiedworkforce.online';
export const PRODUCTION_APP_HOST = 'app.unifiedworkforce.online';

/**
 * Which surface this browser tab is on.
 * - marketing: apex domain (landing only)
 * - app: app subdomain (product)
 * - combined: local/dev — landing at / plus full product routes
 */
export function getSiteMode(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const host = String(hostname || '').toLowerCase();
  if (host === PRODUCTION_MARKETING_HOST || host === `www.${PRODUCTION_MARKETING_HOST}`) {
    return 'marketing';
  }
  if (host === PRODUCTION_APP_HOST) {
    return 'app';
  }
  return 'combined';
}

export function getAppOrigin(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  if (getSiteMode(hostname) === 'combined') {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return '';
  }
  return `https://${PRODUCTION_APP_HOST}`;
}

export function getMarketingOrigin(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  if (getSiteMode(hostname) === 'combined') {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return '';
  }
  return `https://${PRODUCTION_MARKETING_HOST}`;
}

/** Path or absolute URL for a product-app route from the current surface. */
export function appHref(path = '/', hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const mode = getSiteMode(hostname);
  if (mode === 'app' || mode === 'combined') {
    return normalized;
  }
  return `${getAppOrigin(hostname)}${normalized}`;
}

/** Name of the cross-subdomain session indicator cookie. */
export const SESSION_COOKIE_NAME = 'uw_session';

/** Resolve cookie domain for cross-subdomain session indicator. */
export function getCookieDomain(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const host = String(hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || !host) {
    return '';
  }
  if (host.endsWith(PRODUCTION_MARKETING_HOST)) {
    return `.${PRODUCTION_MARKETING_HOST}`;
  }
  const parts = host.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return '';
}

/** Set cross-subdomain session indicator cookie when user is authenticated. */
export function setSessionCookie(role = 'admin', hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain(hostname);
  const domainAttr = domain ? `; domain=${domain}` : '';
  const secureAttr = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; secure' : '';
  // 7 days in seconds, matching JWT refresh token lifetime
  const maxAge = 7 * 24 * 60 * 60;
  const value = encodeURIComponent(role || 'admin');
  document.cookie = `${SESSION_COOKIE_NAME}=${value}; path=/${domainAttr}; max-age=${maxAge}; samesite=lax${secureAttr}`;
}

/** Clear cross-subdomain session indicator cookie on logout or auth failure. */
export function clearSessionCookie(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain(hostname);
  const domainAttr = domain ? `; domain=${domain}` : '';
  document.cookie = `${SESSION_COOKIE_NAME}=; path=/${domainAttr}; expires=Thu, 01 Jan 1970 00:00:00 GMT; samesite=lax`;
  document.cookie = `${SESSION_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; samesite=lax`;
}

/** Read cross-subdomain session indicator cookie value ('admin', 'user', or null). */
export function getSessionCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${SESSION_COOKIE_NAME}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

